import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

// Contingut d'una pàgina interna (HTML del disseny, Contenido/{idioma}/*.html)
// i el comportament que a l'original feien els scripts de la web:
// sliders, filtres i visor de la galeria, "llegir més", fotos que canvien en
// passar per sobre, botons de reserva enganxats i formularis (que s'envien
// al formulari de contacte de BedLoop).
const ContenidoHtml = ({ html, titulo }) => {

    const { t } = useTranslation();
    const contenidorRef = useRef(null);
    const [visor, setVisor] = useState(null); // { fotos: [], index }

    useEffect(() => {
        const arrel = contenidorRef.current;
        if (!arrel) return;
        const netejes = [];
        const escolta = (el, tipus, fn, opcions) => {
            el.addEventListener(tipus, fn, opcions);
            netejes.push(() => el.removeEventListener(tipus, fn, opcions));
        };

        // -- Textos genèrics (botons, formularis, serveis...): de les
        //    traduccions de la web. Al contingut porten data-t="clau" (text)
        //    o data-t-<atribut>="clau" (placeholder, alt, value...).
        arrel.querySelectorAll("[data-t]").forEach((el) => {
            // El text pot ser dins un enllaç (p.ex. <div><a>Reserva hotel</a></div>):
            // es canvia el de l'element més intern, sense perdre l'enllaç.
            let desti = el;
            while (desti.children.length === 1 && desti.textContent.trim() === desti.children[0].textContent.trim()) {
                desti = desti.children[0];
            }
            desti.textContent = t(el.getAttribute("data-t"));
        });
        ["placeholder", "alt", "value", "data-read-more", "data-read-less"].forEach((atribut) => {
            arrel.querySelectorAll("[data-t-" + atribut + "]").forEach((el) => {
                el.setAttribute(atribut, t(el.getAttribute("data-t-" + atribut)));
            });
        });

        // -- Sliders: controls amb data-slider="#id" -> pista amb scroll natiu.
        arrel.querySelectorAll("[data-slider]").forEach((controls) => {
            const pista = arrel.querySelector(controls.getAttribute("data-slider"));
            if (!pista) return;
            pista.classList.add("hc-js-slider");
            const elements = () => Array.from(pista.children);
            const total = controls.querySelector(".js-total-slides");
            const actual = controls.querySelector(".js-current-index");
            if (total) total.textContent = elements().length;
            const pas = () => (elements()[0]?.getBoundingClientRect().width || pista.clientWidth);
            const [prev, next] = controls.querySelectorAll(".c-slider-controls__button");
            const actualitza = () => {
                const index = Math.round(pista.scrollLeft / pas());
                if (actual) actual.textContent = Math.min(index + 1, elements().length);
            };
            const mou = (direccio) => {
                const max = pista.scrollWidth - pista.clientWidth;
                let desti = pista.scrollLeft + direccio * pas();
                // En bucle, com a l'original.
                if (desti > max + 2) desti = 0;
                if (desti < -2) desti = max;
                pista.scrollTo({ left: desti, behavior: "smooth" });
            };
            if (prev) escolta(prev, "click", () => mou(-1));
            if (next) escolta(next, "click", () => mou(1));
            escolta(pista, "scroll", actualitza, { passive: true });
        });

        // -- "Llegir més" (data-read-more / data-read-less).
        arrel.querySelectorAll(".c-read-more").forEach((bloc) => {
            const contingut = bloc.querySelector(".c-read-more__content");
            const boto = bloc.querySelector(".c-read-more__button");
            if (!contingut || !boto) return;
            const primer = contingut.firstElementChild;
            const tancat = () => (primer ? primer.offsetHeight : 0);
            if (contingut.scrollHeight <= tancat() + 4) {
                boto.style.display = "none";
                return;
            }
            boto.classList.remove("hc-hidden");
            let obert = false;
            contingut.style.height = tancat() + "px";
            const text = boto.querySelector("span") || boto;
            escolta(boto, "click", (e) => {
                e.preventDefault();
                obert = !obert;
                contingut.style.height = (obert ? contingut.scrollHeight : tancat()) + "px";
                bloc.classList.toggle("c-read-more--open", obert);
                text.textContent = obert ? boto.getAttribute("data-read-less") : boto.getAttribute("data-read-more");
            });
        });

        // -- Fotos que canvien en passar per sobre (data-target-image).
        arrel.querySelectorAll("[data-target-image]").forEach((item) => {
            const seccio = item.closest("section") || arrel;
            const activa = () => {
                const id = item.getAttribute("data-target-image");
                seccio.querySelectorAll("img[data-id]").forEach((img) => {
                    img.setAttribute("data-active", img.getAttribute("data-id") === id ? "true" : "false");
                });
            };
            escolta(item, "mouseenter", activa);
            escolta(item, "focusin", activa);
        });

        // -- Galeria: filtres (botons i desplegable), "veure'n més" (de 9 en 9)
        //    i visor.
        arrel.querySelectorAll(".c-gallery__content").forEach((graella) => {
            const seccio = graella.closest("section") || arrel;
            const items = Array.from(graella.querySelectorAll(".c-gallery__item"));
            const filtres = seccio.querySelector(".c-gallery__filters");
            const botons = filtres ? Array.from(filtres.querySelectorAll("button[data-filter]")) : [];
            const desplegable = filtres ? filtres.querySelector("select") : null;
            const mes = seccio.querySelector(".c-gallery-button");
            const PER_PAGINA = 9;
            let filtre = "all";
            let limit = PER_PAGINA;
            const aplica = () => {
                let visibles = 0;
                let coincidents = 0;
                items.forEach((item) => {
                    const grups = JSON.parse(item.getAttribute("data-groups") || "[]");
                    const coincideix = filtre === "all" || grups.includes(filtre);
                    if (coincideix) coincidents++;
                    const mostra = coincideix && visibles < limit;
                    if (mostra) visibles++;
                    item.style.display = mostra ? "" : "none";
                });
                if (mes) mes.style.display = coincidents > visibles ? "" : "none";
                botons.forEach((b) => {
                    const es = b.getAttribute("data-filter") === filtre;
                    b.classList.toggle("active", es);
                    b.setAttribute("data-active", es ? "true" : "false");
                });
            };
            const filtra = (nou) => {
                filtre = nou;
                limit = PER_PAGINA;
                aplica();
            };
            botons.forEach((b) => escolta(b, "click", () => filtra(b.getAttribute("data-filter"))));
            if (desplegable) {
                escolta(desplegable, "change", () => filtra(desplegable.selectedOptions[0]?.getAttribute("data-filter") || "all"));
            }
            if (mes) {
                escolta(mes, "click", (e) => {
                    e.preventDefault();
                    limit += PER_PAGINA;
                    aplica();
                });
            }
            aplica();
        });

        const fotosVisor = (el) => {
            const grup = el.closest(".c-gallery__content, .c-gallery, .gallery-section") || arrel;
            return Array.from(grup.querySelectorAll(".c-gallery__item[href], .c-gallery-item[href]"))
                .filter((item) => item.style.display !== "none")
                .map((item) => ({ src: item.getAttribute("href"), alt: item.querySelector("img")?.getAttribute("alt") || "" }));
        };
        arrel.querySelectorAll(".c-gallery__item[href], .c-gallery-item[href]").forEach((item) => {
            item.setAttribute("role", "button");
            item.setAttribute("tabindex", "0");
            const obre = (e) => {
                e.preventDefault();
                const fotos = fotosVisor(item);
                const index = Math.max(0, fotos.findIndex((f) => f.src === item.getAttribute("href")));
                setVisor({ fotos, index });
            };
            escolta(item, "click", obre);
            escolta(item, "keydown", (e) => { if (e.key === "Enter" || e.key === " ") obre(e); });
        });
        // Galeries curtes (bar, restaurant, events...): totes les fotos ja es
        // veuen, així que "Veure més imatges" obre el visor des de la primera.
        arrel.querySelectorAll(".gallery-section").forEach((seccio) => {
            if (seccio.querySelector(".c-gallery__content")) return;
            const boto = seccio.querySelector(".c-gallery-button button");
            const primera = seccio.querySelector(".c-gallery-item[href]");
            if (!boto || !primera) return;
            escolta(boto, "click", (e) => {
                e.preventDefault();
                setVisor({ fotos: fotosVisor(primera), index: 0 });
            });
        });
        // -- Botons "Reserva hotel / restaurant": enganxats sota la capçalera
        //    a escriptori un cop passat el hero.
        const hero = arrel.querySelector(".c-hero");
        const sticky = arrel.querySelector(".c-sticky-widgets");
        if (hero && sticky) {
            const alFerScroll = () => sticky.classList.toggle("c-sticky-widgets--stuck", hero.getBoundingClientRect().bottom < 80);
            alFerScroll();
            escolta(window, "scroll", alFerScroll, { passive: true });
        }

        // -- Formularis -> formulari de contacte de BedLoop (/enviacontacto).
        arrel.querySelectorAll("form[data-hc-form]").forEach((form) => {
            const avis = document.createElement("p");
            avis.className = "hc-form-avis";
            avis.setAttribute("role", "status");
            form.appendChild(avis);

            escolta(form, "submit", (e) => {
                e.preventDefault();
                const legal = form.querySelector('input[type="checkbox"][name*="legal"], input[type="checkbox"][name*="privacy"]');
                if (legal && !legal.checked) {
                    avis.textContent = t("Todos los campos obligatorios") + ": " + t("consentimiento");
                    avis.dataset.estat = "error";
                    return;
                }
                const dades = new FormData(form);
                const valor = (...noms) => noms.map((n) => (dades.get(n) || "").toString().trim()).find(Boolean) || "";
                const nom = valor("name", "nombre", "first_name") + (valor("surname", "last_name", "apellidos") ? " " + valor("surname", "last_name", "apellidos") : "");
                const correu = valor("email", "mail");
                if (!nom || !correu) {
                    avis.textContent = t("Todos los campos obligatorios");
                    avis.dataset.estat = "error";
                    return;
                }
                const linies = [titulo];
                form.querySelectorAll("input, select, textarea").forEach((camp) => {
                    if (!camp.name || camp.type === "hidden" || camp.type === "submit") return;
                    if ((camp.type === "checkbox" || camp.type === "radio") && !camp.checked) return;
                    if (["name", "email", "company_website"].includes(camp.name) || /legal|privacy/.test(camp.name)) return;
                    const valorCamp = camp.value.trim();
                    if (!valorCamp) return;
                    const etiqueta = form.querySelector('label[for="' + camp.id + '"]')?.textContent.trim() || camp.name;
                    linies.push(etiqueta + ": " + valorCamp);
                });
                const boto = form.querySelector('[type="submit"]');
                if (boto) boto.disabled = true;
                avis.textContent = "";
                const token = document.getElementById("meta_token")?.getAttribute("content");
                axios.post("/enviacontacto", {
                    yourname: nom,
                    contactEmail: correu,
                    Phone: valor("phone", "telefono", "telephone") || "-",
                    contactMessage: linies.join("<br/>"),
                    company_website: valor("company_website"),
                }, { headers: { "X-CSRF-TOKEN": token } })
                    .then(() => {
                        form.reset();
                        avis.textContent = t("Contacto enviado");
                        avis.dataset.estat = "ok";
                    })
                    .catch(() => {
                        avis.textContent = t("Contacto no enviado");
                        avis.dataset.estat = "error";
                    })
                    .finally(() => { if (boto) boto.disabled = false; });
            });

            // Camp parany anti-spam (el mateix que el formulari de contacte).
            const parany = document.createElement("input");
            parany.type = "text";
            parany.name = "company_website";
            parany.tabIndex = -1;
            parany.autocomplete = "off";
            parany.className = "hc-parany";
            parany.setAttribute("aria-hidden", "true");
            form.appendChild(parany);
        });

        // El correu escrit a la franja del newsletter (?email=) ja omplert.
        const correuUrl = new URLSearchParams(window.location.search).get("email");
        if (correuUrl) {
            arrel.querySelectorAll('form[data-hc-form] input[type="email"]').forEach((camp) => {
                if (!camp.value) camp.value = correuUrl;
            });
        }

        return () => netejes.forEach((neteja) => neteja());
    }, [html]);

    // Visor: teclat i scroll de la pàgina bloquejat mentre és obert.
    useEffect(() => {
        if (!visor) return;
        const alTeclear = (e) => {
            if (e.key === "Escape") setVisor(null);
            if (e.key === "ArrowRight") setVisor((v) => ({ ...v, index: (v.index + 1) % v.fotos.length }));
            if (e.key === "ArrowLeft") setVisor((v) => ({ ...v, index: (v.index - 1 + v.fotos.length) % v.fotos.length }));
        };
        const overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", alTeclear);
        return () => {
            window.removeEventListener("keydown", alTeclear);
            document.body.style.overflow = overflow;
        };
    }, [visor !== null]);

    const foto = visor ? visor.fotos[visor.index] : null;

    return (
        <>
            <div className="hc-contingut" ref={contenidorRef} dangerouslySetInnerHTML={{ __html: html }}></div>

            {foto && (
                <div className="hc-visor" role="dialog" aria-modal="true" onClick={() => setVisor(null)}>
                    <button type="button" className="hc-visor__tancar" aria-label={t("Cerrar")} onClick={() => setVisor(null)}>×</button>
                    {visor.fotos.length > 1 && (
                        <button type="button" className="hc-visor__nav hc-visor__nav--prev" aria-label={t("Anterior")} onClick={(e) => { e.stopPropagation(); setVisor({ ...visor, index: (visor.index - 1 + visor.fotos.length) % visor.fotos.length }); }}>‹</button>
                    )}
                    <figure className="hc-visor__figura" onClick={(e) => e.stopPropagation()}>
                        <img src={foto.src} alt={foto.alt} />
                        <figcaption>{visor.index + 1} / {visor.fotos.length}</figcaption>
                    </figure>
                    {visor.fotos.length > 1 && (
                        <button type="button" className="hc-visor__nav hc-visor__nav--next" aria-label={t("Siguiente")} onClick={(e) => { e.stopPropagation(); setVisor({ ...visor, index: (visor.index + 1) % visor.fotos.length }); }}>›</button>
                    )}
                </div>
            )}
        </>
    );
};

export default ContenidoHtml;
